import React, { useState, useEffect } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";

export const Route = createFileRoute("/SaasScalability")({
  component: SaasScalability,
});

type Tab = "API_MONITOR" | "STRIPE_INTEGRATION" | "INFRASTRUCTURE";

function SaasScalability() {
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState<Tab>("API_MONITOR");
  const [concurrentUsers, setConcurrentUsers] = useState(245);
  const [revenue, setRevenue] = useState(12840.50);
  const [isProcessing, setIsProcessing] = useState(false);
  const [logs, setLogs] = useState<string[]>([
    "[AUTH] JWT Verification Success - user_882",
    "[API] GET /v1/subscriptions - 200 OK (34ms)",
    "[STRIPE] Webhook received: payment_intent.succeeded"
  ]);

  useEffect(() => {
    const interval = setInterval(() => {
      setConcurrentUsers(prev => prev + Math.floor(Math.random() * 5) - 2);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const handleCheckoutSimulation = () => {
    setIsProcessing(true);
    const newLogs = [
      "[POST] /v1/checkout - 202 Accepted",
      "[STRIPE] Creating PaymentIntent: $49.00",
      "[ASYNC] Handing off to background worker..."
    ];
    setLogs(prev => [...newLogs, ...prev].slice(0, 8));

    setTimeout(() => {
      setIsProcessing(false);
      setRevenue(prev => prev + 49.00);
      setLogs(prev => ["[SUCCESS] Webhook: invoice.paid - Provisioning Seats", ...prev].slice(0, 8));
    }, 2500);
  };

  return (
    <div style={containerStyle}>
      <nav style={navStyle}>
        <div style={navBrand} onClick={() => navigate({ to: "/Portfolio" })} style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: "12px" }}>
          <div style={avatarStyle}>JF</div>
          <span style={{ fontWeight: "bold", letterSpacing: "1px" }}>CASE_STUDY_02</span>
        </div>
        <div style={navLinks}>
          <button onClick={() => navigate({ to: "/Portfolio" })} style={backButtonStyle}>
            ← BACK_TO_PORTFOLIO
          </button>
        </div>
      </nav>

      <main style={mainContent}>
        <header style={headerSection}>
          <div style={labelStyle}>SaaS_ENGINEERING</div>
          <h1 style={titleStyle}>Scalability Engine & Stripe API</h1>
          <p style={subtitleStyle}>Building high-concurrency RESTful architectures for PCI-compliant global payments.</p>
          <div style={tagRow}>
            {["ASP.NET_CORE", "STRIPE_SDK", "REDIS", "ASYNC_AWAIT", "REST_API", "PCI_COMPLIANCE"].map((t) => (
              <span key={t} style={tagStyle}>{t}</span>
            ))}
          </div>
        </header>

        <section style={sectionBox}>
          <span style={sectionLabel}>01. PROJECT_SCOPE</span>
          <div style={gridTwo}>
            <div>
              <h3 style={smallHeading}>THE MISSION</h3>
              <p style={paragraph}>
                Develop a robust backend capable of handling hundreds of concurrent users while maintaining strict data integrity. The primary challenge was ensuring the UI remained responsive during long-running payment handshakes.
              </p>
            </div>
            <div>
              <h3 style={smallHeading}>THE STACK</h3>
              <p style={paragraph}>
                Utilized **ASP.NET Core** for the REST API layer, leveraging **Dapper** for high-performance data access and **Stripe's .NET SDK** for secure payment orchestration.
              </p>
            </div>
          </div>
        </section>

        <section style={sectionBox}>
          <span style={sectionLabel}>02. LIVE_API_ORCHESTRATION</span>
          <div style={webWindow}>
            <div style={webTabRow}>
              {(['API_MONITOR', 'STRIPE_INTEGRATION', 'INFRASTRUCTURE'] as Tab[]).map(t => (
                <span key={t} onClick={() => setActiveTab(t)}
                  style={{ ...webTab, color: activeTab === t ? '#3b82f6' : '#555', borderBottom: activeTab === t ? '2px solid #3b82f6' : 'none' }}>
                  {t}
                </span>
              ))}
            </div>

            <div style={apiGridContainer}>
              <div style={{ minHeight: '300px' }}>
                {activeTab === 'API_MONITOR' && (
                  <div>
                    <div style={metricGrid}>
                      <div style={metricCard}>
                        <h2 style={metricValue}>{concurrentUsers}</h2>
                        <span style={metricLabel}>CONCURRENT_SESSIONS</span>
                      </div>
                      <div style={metricCard}>
                        <h2 style={{ ...metricValue, color: '#10b981' }}>${revenue.toLocaleString()}</h2>
                        <span style={metricLabel}>PROCESSED_VIA_STRIPE</span>
                      </div>
                    </div>
                    <div style={webLogTerminal}>
                      {logs.map((log, i) => (
                        <div key={i} style={{ 
                          color: log.includes('SUCCESS') ? '#10b981' : log.includes('STRIPE') ? '#f59e0b' : '#3b82f6', 
                          marginBottom: '4px', borderLeft: '2px solid #222', paddingLeft: '8px'
                        }}>
                          {log}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {activeTab === 'STRIPE_INTEGRATION' && (
                  <div style={{ padding: '10px' }}>
                    <h4 style={smallHeading}>PAYMENT_FLOW_LOGIC</h4>
                    <p style={{ ...paragraph, fontSize: '12px', marginBottom: '20px' }}>
                      Implemented **Stripe PaymentIntents**. The system uses webhooks to update order status asynchronously, preventing "Double Charge" issues.
                    </p>
                    <div style={codeBlock}>
                      <code>{`// Stripe Webhook Logic\nif (event.Type == "payment_intent.succeeded") {\n  await _orderService.FulfillAsync(intent.Id);\n}`}</code>
                    </div>
                    <button style={webButton} onClick={handleCheckoutSimulation} disabled={isProcessing}>
                      {isProcessing ? 'COMMUNICATING_WITH_STRIPE...' : 'EXECUTE_TEST_CHECKOUT ($49)'}
                    </button>
                  </div>
                )}

                {activeTab === 'INFRASTRUCTURE' && (
                  <div style={{ padding: '10px' }}>
                    <h4 style={smallHeading}>MULTI-THREADED_PERFORMANCE</h4>
                    <p style={{ ...paragraph, fontSize: '12px' }}>
                      API endpoints designed using the **Asynchronous Request-Reply Pattern**. High-latency tasks are offloaded to background threads to keep the main thread pool clear.
                    </p>
                  </div>
                )}
              </div>

              <div style={infoSidebar}>
                <div style={sideLabel}>SYSTEM_HEALTH</div>
                <div style={healthBar}><div style={{ ...healthFill, width: '94%' }}></div></div>
                <div style={{ fontSize: '10px', color: '#555', marginTop: '5px' }}>LATENCY: 42ms</div>
                <div style={{ marginTop: '30px' }}>
                  <div style={sideLabel}>PCI_STATUS</div>
                  <div style={{ color: '#10b981', fontSize: '11px', fontWeight: 'bold' }}>✓ COMPLIANT</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        

        <section style={sectionBox}>
          <span style={sectionLabel}>03. ARCHITECTURE_BREAKDOWN</span>
          <div style={gridThree}>
            <div style={cardStyle}>
              <div style={cardNum}>01</div>
              <h4 style={cardTitle}>THREAD_SAFETY</h4>
              <p style={cardText}>Used `SemaphoreSlim` to throttle concurrent connections to internal services, preventing resource exhaustion during spikes.</p>
            </div>
            <div style={cardStyle}>
              <div style={cardNum}>02</div>
              <h4 style={cardTitle}>IDEMPOTENT_KEYS</h4>
              <p style={cardText}>Implemented Stripe Idempotency keys for every request, allowing safe retries without duplicate charges.</p>
            </div>
            <div style={cardStyle}>
              <div style={cardNum}>03</div>
              <h4 style={cardTitle}>REST_DESIGN</h4>
              <p style={cardText}>Clean separation of concerns between the API controllers and the background payment workers.</p>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}

/* STYLES */
const containerStyle: React.CSSProperties = { backgroundColor: "#0a0a0a", color: "#e0e0e0", minHeight: "100vh", padding: "120px 24px 60px 24px", fontFamily: "monospace" };
const navStyle: React.CSSProperties = { position: "fixed", top: 0, left: 0, right: 0, height: "70px", backgroundColor: "rgba(10, 10, 10, 0.8)", backdropFilter: "blur(12px)", borderBottom: "1px solid #222", display: "flex", justifyContent: "space-between", alignItems: "center", padding: "0 40px", zIndex: 1000 };
const navBrand: React.CSSProperties = { display: "flex", alignItems: "center", gap: "12px" };
const avatarStyle: React.CSSProperties = { width: "32px", height: "32px", backgroundColor: "#1a1a1a", border: "1px solid #333", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: "12px", fontWeight: "bold", borderRadius: "8px" };
const navLinks: React.CSSProperties = { display: "flex", gap: "30px" };
const backButtonStyle: React.CSSProperties = { backgroundColor: "transparent", border: "1px solid #333", color: "#666", padding: "8px 16px", fontSize: "11px", fontWeight: "bold", cursor: "pointer" };
const mainContent: React.CSSProperties = { maxWidth: "1000px", margin: "0 auto" };
const headerSection: React.CSSProperties = { marginBottom: "60px", borderBottom: "1px solid #1a1a1a", paddingBottom: "40px" };
const titleStyle: React.CSSProperties = { fontSize: "32px", color: "#fff", margin: "10px 0", fontWeight: "bold" };
const subtitleStyle: React.CSSProperties = { fontSize: "16px", color: "#3b82f6", marginBottom: "25px", maxWidth: "600px" };
const labelStyle: React.CSSProperties = { fontSize: "10px", color: "#666", marginBottom: "12px", letterSpacing: "1px" };
const tagRow: React.CSSProperties = { display: "flex", gap: "10px", flexWrap: "wrap" };
const tagStyle: React.CSSProperties = { fontSize: "10px", backgroundColor: "#1a1a1a", border: "1px solid #333", padding: "4px 12px", color: "#aaa", borderRadius: "6px" };
const sectionBox: React.CSSProperties = { marginBottom: "100px" };
const sectionLabel: React.CSSProperties = { fontSize: "10px", color: "#444", letterSpacing: "2px", display: "block", marginBottom: "30px" };
const gridTwo: React.CSSProperties = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "40px" };
const smallHeading: React.CSSProperties = { fontSize: "14px", color: "#fff", marginBottom: "15px", fontWeight: "bold" };
const paragraph: React.CSSProperties = { fontSize: "14px", color: "#888", lineHeight: "1.6", margin: 0 };

const webWindow: React.CSSProperties = { backgroundColor: '#111', border: '1px solid #222', borderRadius: '16px', padding: '30px' };
const webTabRow: React.CSSProperties = { display: 'flex', gap: '20px', borderBottom: '1px solid #222', marginBottom: '30px' };
const webTab: React.CSSProperties = { fontSize: '10px', cursor: 'pointer', paddingBottom: '12px', fontWeight: 'bold', letterSpacing: '1px' };
const apiGridContainer: React.CSSProperties = { display: 'grid', gridTemplateColumns: '1fr 300px', gap: '30px' };
const metricGrid: React.CSSProperties = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '20px' };
const metricCard: React.CSSProperties = { backgroundColor: '#0a0a0a', border: '1px solid #222', padding: '20px', borderRadius: '12px' };
const metricValue: React.CSSProperties = { fontSize: "28px", color: "#fff", margin: 0 };
const metricLabel: React.CSSProperties = { fontSize: "9px", color: "#444", marginTop: '5px', display: 'block' };
const webLogTerminal: React.CSSProperties = { backgroundColor: '#050505', padding: '15px', borderRadius: '8px', fontSize: '11px', height: '160px', overflowY: 'auto' };
const webButton: React.CSSProperties = { width: '100%', backgroundColor: '#3b82f6', color: '#fff', border: 'none', padding: '14px', borderRadius: '8px', fontWeight: 'bold', fontSize: '11px', cursor: 'pointer', marginTop: '20px' };
const codeBlock: React.CSSProperties = { backgroundColor: '#000', padding: '15px', borderRadius: '8px', color: '#888', fontSize: '11px', lineHeight: '1.5', border: '1px solid #222' };

const infoSidebar: React.CSSProperties = { borderLeft: '1px solid #222', paddingLeft: '30px' };
const sideLabel: React.CSSProperties = { fontSize: '9px', color: '#444', marginBottom: '10px' };
const healthBar: React.CSSProperties = { width: '100%', height: '4px', backgroundColor: '#222', borderRadius: '2px' };
const healthFill: React.CSSProperties = { height: '100%', backgroundColor: '#10b981', borderRadius: '2px' };

const gridThree: React.CSSProperties = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "20px" };
const cardStyle: React.CSSProperties = { backgroundColor: "#111", padding: "30px", borderRadius: "16px", border: "1px solid #222", position: "relative" };
const cardNum: React.CSSProperties = { fontSize: "40px", color: "#1a1a1a", fontWeight: "bold", position: "absolute", top: "10px", right: "20px" };
const cardTitle: React.CSSProperties = { fontSize: "14px", color: "#fff", marginBottom: "15px", fontWeight: "bold" };
const cardText: React.CSSProperties = { fontSize: "13px", color: "#888", lineHeight: "1.6" };

export default SaasScalability;